import React, { createContext, useState, useEffect } from 'react';
import {
  SidebarContextType,
  defaultContext,
  SidebarProviderProps,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH_DEFAULT,
  SIDEBAR_EXPANDED_WIDTH_XL,
  XL_BREAKPOINT,
} from './SidebarContextTypes';

const SidebarContext = createContext<SidebarContextType>(defaultContext);

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  // Initialize from localStorage if available
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('sidebarCollapsed');
    return storedValue === 'true';
  });

  // Set sidebar collapsed state and update localStorage
  const setSidebarCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', String(collapsed));

    // Dispatch a custom event for non-React components that need to know about sidebar changes
    window.dispatchEvent(
      new CustomEvent('sidebarStateChanged', {
        detail: { isCollapsed: collapsed },
      })
    );
  };

  // Toggle sidebar state
  const toggleSidebar = () => {
    setSidebarCollapsed(!isCollapsed);
  };

  // Track if we're at XL breakpoint or above
  const [isXlBreakpoint, setIsXlBreakpoint] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= XL_BREAKPOINT;
    }
    return false;
  });

  // Calculate current sidebar width based on collapsed state and screen size
  let sidebarWidth = SIDEBAR_COLLAPSED_WIDTH;
  if (!isCollapsed) {
    sidebarWidth = isXlBreakpoint ? SIDEBAR_EXPANDED_WIDTH_XL : SIDEBAR_EXPANDED_WIDTH_DEFAULT;
  }

  // Listen for window resize to update breakpoint state
  useEffect(() => {
    const handleResize = () => {
      const isXl = window.innerWidth >= XL_BREAKPOINT;
      setIsXlBreakpoint(isXl);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for localStorage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sidebarCollapsed') {
        setIsCollapsed(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const contextValue: SidebarContextType = {
    isCollapsed,
    sidebarWidth,
    toggleSidebar,
    setSidebarCollapsed,
    isXlBreakpoint,
  };

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};

export default SidebarContext;
